import { PencilIcon, PercentBadgeIcon, PlusIcon, TicketIcon, TrashIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { adminService } from '../../services/api';

const CouponManagement = () => {
  const { refreshCoupons, refreshKey } = useAdmin();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);

  const fetchCoupons = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminService.getAllCoupons();
      setCoupons(response.data.coupons);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons, refreshKey]);

  const handleDeleteCoupon = async (couponId) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        setLoading(true);
        // Optimistically update UI by removing the coupon from the list
        setCoupons(prevCoupons => prevCoupons.filter(coupon => coupon._id !== couponId));

        await adminService.deleteCoupon(couponId);
        await refreshCoupons();
        // Refresh from server to ensure consistency
        await fetchCoupons();
      } catch (error) {
        console.error('Failed to delete coupon:', error);
        // Revert optimistic update on error
        await fetchCoupons();
        alert('Failed to delete coupon: ' + (error.response?.data?.message || error.message));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setShowModal(true);
  };

  const handleCreateCoupon = () => {
    setEditingCoupon(null);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-200 border-t-green-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-4xl font-bold bg-gradient-to-r from-[#008000] to-emerald-600 bg-clip-text text-transparent">
            Coupon Management
          </h2>
          <p className="text-gray-600 text-lg font-medium mt-2">Manage discount coupons and promotions</p>
        </div>
        <button
          onClick={handleCreateCoupon}
          className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-shadow flex items-center font-bold"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Coupon
        </button>
      </div>

      {/* Coupons Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {coupons.map((coupon) => (
          <div
            key={coupon._id}
            className="bg-gradient-to-br from-white to-gray-50 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-shadow p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center">
                <div className="p-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg mr-3">
                  <TicketIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{coupon.code}</h3>
                  <p className="text-sm text-gray-600 font-medium">{coupon.description}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditCoupon(coupon)}
                  className="p-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:shadow-md transition-shadow"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteCoupon(coupon._id)}
                  className="p-2 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg hover:shadow-md transition-shadow"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 font-medium">Type:</span>
                <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full border ${coupon.type === 'percentage'
                  ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-[#008000] border-green-200'
                  : 'bg-gradient-to-r from-blue-100 to-sky-100 text-blue-800 border-blue-200'
                  }`}>
                  {coupon.type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 font-medium">Discount:</span>
                <span className="text-sm font-bold text-[#008000] flex items-center">
                  {coupon.type === 'percentage' ? (
                    <>
                      <PercentBadgeIcon className="h-4 w-4 mr-1" />
                      {coupon.value}%
                    </>
                  ) : (
                    `₹${coupon.value}`
                  )}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 font-medium">Min Order:</span>
                <span className="text-sm font-bold text-gray-900">₹{coupon.minimumOrderAmount || 0}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 font-medium">Max Discount:</span>
                <span className="text-sm font-bold text-gray-900">₹{coupon.maximumDiscountAmount || 'No Limit'}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 font-medium">Usage:</span>
                <span className="text-sm font-bold text-gray-900">
                  {coupon.usageCount?.total || 0} / {coupon.usageLimit?.total || 'Unlimited'}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 font-medium">Per User:</span>
                <span className="text-sm font-bold text-gray-900">
                  {coupon.usageLimit?.perUser || 1}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 font-medium">Valid Until:</span>
                <span className="text-sm font-bold text-gray-900">
                  {coupon.validity?.endDate
                    ? new Date(coupon.validity.endDate).toLocaleDateString()
                    : 'No Expiry'}
                </span>
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full border flex-1 justify-center ${coupon.isActive
                ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-[#008000] border-green-200'
                : 'bg-gradient-to-r from-red-100 to-pink-100 text-red-800 border-red-200'
                }`}>
                {coupon.isActive ? '✓ Active' : '✗ Inactive'}
              </span>
              {coupon.isFeatured && (
                <span className="inline-flex px-3 py-1 text-xs font-bold rounded-full border bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border-amber-200 flex-1 justify-center">
                  ⭐ Featured
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Coupon Modal */}
      {showModal && (
        <CouponModal
          coupon={editingCoupon}
          onClose={() => setShowModal(false)}
          onSave={async () => {
            setShowModal(false);
            await refreshCoupons();
            fetchCoupons();
          }}
        />
      )}
    </div>
  );
};

// Coupon Modal Component
const CouponModal = ({ coupon, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    code: coupon?.code || '',
    name: coupon?.name || '',
    description: coupon?.description || '',
    type: coupon?.type || 'percentage',
    value: coupon?.value || '',
    minimumOrderAmount: coupon?.minimumOrderAmount || '',
    maximumDiscountAmount: coupon?.maximumDiscountAmount || '',
    usageLimitTotal: coupon?.usageLimit?.total || '',
    usageLimitPerUser: coupon?.usageLimit?.perUser || 1,
    startDate: coupon?.validity?.startDate ? new Date(coupon.validity.startDate).toISOString().split('T')[0] : '',
    endDate: coupon?.validity?.endDate ? new Date(coupon.validity.endDate).toISOString().split('T')[0] : '',
    isActive: coupon?.isActive ?? true,
    isFeatured: coupon?.isFeatured ?? false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        code: formData.code.trim().toUpperCase(),
        name: formData.name.trim(),
        description: formData.description.trim(),
        type: formData.type,
        value: parseFloat(formData.value),
        minimumOrderAmount: formData.minimumOrderAmount ? parseFloat(formData.minimumOrderAmount) : 0,
        maximumDiscountAmount: formData.maximumDiscountAmount ? parseFloat(formData.maximumDiscountAmount) : undefined,
        usageLimit: {
          total: formData.usageLimitTotal ? parseInt(formData.usageLimitTotal) : undefined,
          perUser: formData.usageLimitPerUser ? parseInt(formData.usageLimitPerUser) : 1
        },
        validity: {
          startDate: formData.startDate ? new Date(formData.startDate) : new Date(),
          endDate: formData.endDate ? new Date(formData.endDate) : undefined
        },
        isActive: formData.isActive,
        isFeatured: formData.isFeatured
      };

      if (coupon) {
        await adminService.updateCoupon(coupon._id, submitData);
      } else {
        await adminService.createCoupon(submitData);
      }
      onSave();
    } catch (error) {
      console.error('Failed to save coupon:', error);
      alert('Failed to save coupon: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-white to-gray-50 backdrop-blur-lg border border-gray-200 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-3xl font-bold bg-gradient-to-r from-[#008000] to-emerald-600 bg-clip-text text-transparent">
                {coupon ? 'Edit Coupon' : 'Create Coupon'}
              </h3>
              <p className="text-gray-600 font-medium mt-2">
                {coupon ? 'Update coupon details' : 'Create a new discount coupon'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl hover:shadow-lg transition-shadow"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Coupon Code</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
                  placeholder="Enter coupon code"
                  required
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Coupon Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
                  placeholder="Enter coupon name"
                  required
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
                  rows="3"
                  placeholder="Describe this coupon"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Discount Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (₹)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Discount Value {formData.type === 'percentage' ? '(%)' : '(₹)'}
                </label>
                <input
                  type="number"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
                  placeholder={formData.type === 'percentage' ? 'Enter percentage' : 'Enter amount'}
                  required
                  min="0"
                  max={formData.type === 'percentage' ? "100" : undefined}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Minimum Order Amount (₹)</label>
                <input
                  type="number"
                  value={formData.minimumOrderAmount}
                  onChange={(e) => setFormData({ ...formData, minimumOrderAmount: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
                  placeholder="Optional minimum order"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Maximum Discount Amount (₹)</label>
                <input
                  type="number"
                  value={formData.maximumDiscountAmount}
                  onChange={(e) => setFormData({ ...formData, maximumDiscountAmount: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
                  placeholder="Optional maximum discount"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Total Usage Limit</label>
                <input
                  type="number"
                  value={formData.usageLimitTotal}
                  onChange={(e) => setFormData({ ...formData, usageLimitTotal: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
                  placeholder="Optional total usage limit"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Per User Usage Limit</label>
                <input
                  type="number"
                  value={formData.usageLimitPerUser}
                  onChange={(e) => setFormData({ ...formData, usageLimitPerUser: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
                  placeholder="Usage limit per user"
                  min="1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
                  required
                />
              </div>
            </div>

            <div className="flex flex-col space-y-3">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-5 h-5 text-[#008000] bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                  id="isActive"
                />
                <label htmlFor="isActive" className="text-sm font-bold text-gray-700">
                  Active Coupon
                </label>
              </div>
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={formData.isFeatured}
                  onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                  className="w-5 h-5 text-amber-600 bg-gray-100 border-gray-300 rounded focus:ring-amber-500 focus:ring-2"
                  id="isFeatured"
                />
                <label htmlFor="isFeatured" className="text-sm font-bold text-gray-700">
                  Featured Offer (Show on landing page)
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 rounded-xl text-sm font-bold text-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 hover:shadow-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl text-sm font-bold hover:from-[#008000] hover:to-emerald-600 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <TicketIcon className="h-4 w-4 mr-2" />
                    {coupon ? 'Update Coupon' : 'Create Coupon'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CouponManagement;
